import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { StreamActivityNavigationRequest } from "@/features/study-group-stream/hooks/useStreamActivityState";
import type { StreamActivity } from "@/features/study-group-stream/types/streamActivity";
import { buildExistingPostPayload } from "@/features/study-group-stream/utils/postMutationPayload";
import { activityKey } from "@/features/study-group-stream/utils/streamActivity";
import { postsApi } from "@/lib/humhub-api/posts";
import { streamReadApi } from "@/lib/humhub-api/streamRead";
import type { Comment, StreamContent } from "@/lib/humhub-api/types";

export interface StreamActivityNavigationResolution {
  keys: string[];
  target: StreamActivity | null;
}

interface UseStreamActivityNavigationOptions {
  request: StreamActivityNavigationRequest | null | undefined;
  streamContents: StreamContent[];
  scopeKey?: string;
  refetch: () => Promise<unknown>;
  afterRefetch?: () => Promise<void> | void;
  positioningTimeoutMs?: number;
  isPostAllowed?: (post: StreamContent) => boolean;
  onResolved: (resolution: StreamActivityNavigationResolution) => void;
  onFailed: (reason?: "stream-refresh" | "target-position") => void;
}

const DEFAULT_POSITIONING_TIMEOUT_MS = 5_000;
const NAVIGATION_HIGHLIGHT_DURATION_MS = 3_000;
const READ_OBSERVER_RESUME_DELAY_MS = 500;
export const CATCH_UP_POSITIONING_EVENT = "stream-catch-up-positioning";

const getResponseStatus = (error: unknown) => {
  if (!error || typeof error !== "object" || !("response" in error)) {
    return null;
  }
  const response = error.response;
  if (!response || typeof response !== "object" || !("status" in response)) {
    return null;
  }
  return typeof response.status === "number" ? response.status : null;
};

const allowAllPosts = () => true;

const getActivityElement = (activity: StreamActivity) =>
  document.querySelector<HTMLElement>(
    activity.type === "post"
      ? `[data-post-content-id="${activity.postContentId}"]`
      : `[data-comment-id="${activity.id}"]`,
  );

const mergeTargetComment = (
  post: StreamContent,
  targetComment: Comment | null,
  parentComment: Comment | null,
): StreamContent => {
  if (!targetComment) return post;
  let injectedComment = targetComment;
  if (parentComment && parentComment.id !== targetComment.id) {
    const replies = [...(parentComment.comments ?? [])];
    if (!replies.some((reply) => reply.id === targetComment.id)) {
      replies.push(targetComment);
    }
    injectedComment = {
      ...parentComment,
      comments: replies,
      commentsCount: Math.max(parentComment.commentsCount ?? 0, replies.length),
    };
  }
  const latest = post.comments.latest.filter(
    (comment) => comment.id !== injectedComment.id,
  );
  latest.push(injectedComment);
  return {
    ...post,
    comments: {
      total: Math.max(post.comments.total, latest.length),
      latest,
    },
  };
};

type ScopedNavigationRequest = {
  request: StreamActivityNavigationRequest;
  requestId: number;
  scopeKey: string;
};

export const useStreamActivityNavigation = ({
  request,
  streamContents,
  scopeKey = "default",
  refetch,
  afterRefetch,
  positioningTimeoutMs = DEFAULT_POSITIONING_TIMEOUT_MS,
  isPostAllowed = allowAllPosts,
  onResolved,
  onFailed,
}: UseStreamActivityNavigationOptions) => {
  const [targetedPost, setTargetedPost] = useState<StreamContent | null>(null);
  const [targetActivity, setTargetActivity] = useState<StreamActivity | null>(
    null,
  );
  const [highlightActivity, setHighlightActivity] =
    useState<StreamActivity | null>(null);
  const resolutionKeysRef = useRef<string[]>([]);
  const finishedRequestRef = useRef<ScopedNavigationRequest | null>(null);
  const activeRequestRef = useRef<ScopedNavigationRequest | null>(null);
  const scopeKeyRef = useRef(scopeKey);
  const requestScopeRef = useRef<ScopedNavigationRequest | null>(null);
  const streamContentsRef = useRef(streamContents);
  const refetchRef = useRef(refetch);
  const afterRefetchRef = useRef(afterRefetch);
  const isPostAllowedRef = useRef(isPostAllowed);
  const onResolvedRef = useRef(onResolved);
  const onFailedRef = useRef(onFailed);
  const positioningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resumeReadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  streamContentsRef.current = streamContents;
  refetchRef.current = refetch;
  afterRefetchRef.current = afterRefetch;
  isPostAllowedRef.current = isPostAllowed;
  onResolvedRef.current = onResolved;
  onFailedRef.current = onFailed;
  const isCurrentScope = scopeKeyRef.current === scopeKey;
  const scopedRequest = useMemo<ScopedNavigationRequest | null>(
    () =>
      request
        ? {
            request,
            requestId: request.requestId,
            scopeKey,
          }
        : null,
    [request, scopeKey],
  );

  useEffect(() => {
    if (scopeKeyRef.current === scopeKey) return;
    const abandonedRequest =
      requestScopeRef.current !== null &&
      finishedRequestRef.current !== requestScopeRef.current;
    scopeKeyRef.current = scopeKey;
    activeRequestRef.current = null;
    finishedRequestRef.current = null;
    if (positioningTimerRef.current) {
      clearTimeout(positioningTimerRef.current);
      positioningTimerRef.current = null;
    }
    if (highlightTimerRef.current) {
      clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = null;
    }
    if (resumeReadTimerRef.current) {
      clearTimeout(resumeReadTimerRef.current);
      resumeReadTimerRef.current = null;
    }
    setTargetActivity(null);
    setTargetedPost(null);
    setHighlightActivity(null);
    if (abandonedRequest) {
      window.dispatchEvent(
        new CustomEvent(CATCH_UP_POSITIONING_EVENT, {
          detail: { isPositioning: false },
        }),
      );
      onFailedRef.current("target-position");
    }
  }, [scopeKey]);

  const resumeVisibleRead = useCallback(() => {
    if (resumeReadTimerRef.current) {
      clearTimeout(resumeReadTimerRef.current);
    }
    resumeReadTimerRef.current = setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent(CATCH_UP_POSITIONING_EVENT, {
          detail: { isPositioning: false },
        }),
      );
      resumeReadTimerRef.current = null;
    }, READ_OBSERVER_RESUME_DELAY_MS);
  }, []);

  const finish = useCallback(
    (target: StreamActivity | null) => {
      if (
        !request ||
        !scopedRequest ||
        activeRequestRef.current !== scopedRequest ||
        finishedRequestRef.current === scopedRequest
      ) {
        return;
      }
      if (positioningTimerRef.current) {
        clearTimeout(positioningTimerRef.current);
        positioningTimerRef.current = null;
      }
      finishedRequestRef.current = scopedRequest;
      activeRequestRef.current = null;
      if (target) {
        if (highlightTimerRef.current) {
          clearTimeout(highlightTimerRef.current);
        }
        setHighlightActivity(target);
        highlightTimerRef.current = setTimeout(() => {
          setHighlightActivity(null);
          highlightTimerRef.current = null;
        }, NAVIGATION_HIGHLIGHT_DURATION_MS);
      }
      resumeVisibleRead();
      onResolvedRef.current({ keys: resolutionKeysRef.current, target });
    },
    [request, resumeVisibleRead, scopedRequest],
  );

  const locateFrom = useCallback(
    async (startIndex: number) => {
      if (!request || !scopedRequest) return;
      for (let index = 0; index < request.activities.length; index += 1) {
        if (index < startIndex) continue;
        const activity = request.activities[index];
        const existingPost = streamContentsRef.current.find(
          (post) => post.id === activity.postContentId,
        );
        if (existingPost) {
          if (!isPostAllowedRef.current(existingPost)) continue;
          if (activeRequestRef.current !== scopedRequest) return;
          resolutionKeysRef.current = request.activities
            .slice(index)
            .map(activityKey);
          setTargetedPost(null);
          setTargetActivity(activity);
          return;
        }

        try {
          const post = await postsApi.getPostByContentId(
            activity.postContentId,
          );
          if (activeRequestRef.current !== scopedRequest) return;
          const targetedPost = buildExistingPostPayload(post);
          if (!isPostAllowedRef.current(targetedPost)) continue;
          resolutionKeysRef.current = request.activities
            .slice(index)
            .map(activityKey);
          setTargetedPost(targetedPost);
          setTargetActivity(activity);
          return;
        } catch (error) {
          const status = getResponseStatus(error);
          if (status === 403 || status === 404) continue;
          if (activeRequestRef.current === scopedRequest) {
            resolutionKeysRef.current = [];
            finish(null);
          }
          return;
        }
      }

      if (activeRequestRef.current !== scopedRequest) return;
      resolutionKeysRef.current = [];
      finish(null);
    },
    [finish, request, scopedRequest],
  );

  useEffect(() => {
    if (!request || !scopedRequest) {
      activeRequestRef.current = null;
      requestScopeRef.current = null;
      setTargetActivity(null);
      return;
    }
    if (
      requestScopeRef.current?.request === request &&
      requestScopeRef.current.scopeKey !== scopeKey
    ) {
      return;
    }
    requestScopeRef.current = scopedRequest;

    activeRequestRef.current = scopedRequest;
    finishedRequestRef.current = null;
    setTargetActivity(null);
    setTargetedPost(null);
    window.dispatchEvent(
      new CustomEvent(CATCH_UP_POSITIONING_EVENT, {
        detail: { isPositioning: true },
      }),
    );

    positioningTimerRef.current = setTimeout(() => {
      if (activeRequestRef.current !== scopedRequest) return;
      resolutionKeysRef.current = [];
      setTargetActivity(null);
      setTargetedPost(null);
      finish(null);
    }, positioningTimeoutMs);

    const resolveTarget = async () => {
      const hiddenActivityInVisiblePost = request.activities.find(
        (activity) =>
          activity.type !== "post" &&
          !getActivityElement(activity) &&
          document.querySelector<HTMLElement>(
            `[data-post-content-id="${activity.postContentId}"]`,
          ) != null,
      );
      const canLoadActivityContext = Boolean(
        request.contextActivityId && request.contextSpaceId,
      );
      if (hiddenActivityInVisiblePost && !canLoadActivityContext) {
        try {
          await refetchRef.current();
        } catch {
          if (activeRequestRef.current === scopedRequest) {
            activeRequestRef.current = null;
            resumeVisibleRead();
            onFailedRef.current("stream-refresh");
          }
          return;
        }
        if (activeRequestRef.current !== scopedRequest) return;
        resolutionKeysRef.current = request.activities.map(activityKey);
        setTargetActivity(hiddenActivityInVisiblePost);
        return;
      }

      const firstActivity = request.activities[0];
      const firstActivityPost = firstActivity
        ? streamContentsRef.current.find(
            (post) => post.id === firstActivity.postContentId,
          )
        : undefined;
      if (
        firstActivity?.type !== "post" &&
        firstActivityPost &&
        isPostAllowedRef.current(firstActivityPost) &&
        !canLoadActivityContext
      ) {
        resolutionKeysRef.current = request.activities.map(activityKey);
        setTargetedPost(null);
        setTargetActivity(firstActivity);
        return;
      }

      for (let index = 0; index < request.activities.length; index += 1) {
        const activity = request.activities[index];
        const element = getActivityElement(activity);
        if (!element) continue;
        resolutionKeysRef.current = request.activities
          .slice(index)
          .map(activityKey);
        if (activity.type === "post") {
          setTargetActivity(activity);
          return;
        }
        element.scrollIntoView({ behavior: "auto", block: "center" });
        finish(activity);
        return;
      }

      const visibleParent = request.activities
        .map((activity) => ({
          activity,
          element: document.querySelector<HTMLElement>(
            `[data-post-content-id="${activity.postContentId}"]`,
          ),
        }))
        .find(({ element }) => element != null);
      visibleParent?.element?.scrollIntoView({
        behavior: "auto",
        block: "center",
      });

      if (request.contextActivityId && request.contextSpaceId) {
        try {
          const context = await streamReadApi.getActivityContext(
            request.contextSpaceId,
            request.contextActivityId,
          );
          if (
            scopeKeyRef.current !== scopedRequest.scopeKey ||
            activeRequestRef.current !== scopedRequest
          )
            return;
          const activity = request.activities[0];
          const contextPost = mergeTargetComment(
            buildExistingPostPayload(context.post),
            context.targetComment,
            context.parentComment,
          );
          if (
            !activity ||
            (!visibleParent && !isPostAllowedRef.current(contextPost))
          ) {
            resolutionKeysRef.current = [];
            finish(null);
            return;
          }
          resolutionKeysRef.current = request.activities.map(activityKey);
          setTargetedPost(contextPost);
          setTargetActivity(activity);
          return;
        } catch (error) {
          const status = getResponseStatus(error);
          if (status === 403 || status === 404) {
            resolutionKeysRef.current = [];
            finish(null);
            return;
          }
        }
      }

      try {
        await refetchRef.current();
      } catch {
        if (activeRequestRef.current === scopedRequest) {
          activeRequestRef.current = null;
          resumeVisibleRead();
          onFailedRef.current("stream-refresh");
        }
        return;
      }

      if (activeRequestRef.current !== scopedRequest) return;

      try {
        await afterRefetchRef.current?.();
      } catch {
        if (activeRequestRef.current === scopedRequest) {
          resolutionKeysRef.current = [];
          finish(null);
        }
        return;
      }

      if (activeRequestRef.current !== scopedRequest) return;
      if (request.activities.length === 0) {
        resolutionKeysRef.current = [];
        finish(null);
        return;
      }

      await locateFrom(0);
    };

    void resolveTarget();
    return () => {
      if (activeRequestRef.current === scopedRequest) {
        activeRequestRef.current = null;
      }
      if (positioningTimerRef.current) {
        clearTimeout(positioningTimerRef.current);
        positioningTimerRef.current = null;
      }
    };
  }, [
    finish,
    locateFrom,
    positioningTimeoutMs,
    request,
    resumeVisibleRead,
    scopeKey,
    scopedRequest,
  ]);

  useEffect(() => {
    if (
      !targetActivity ||
      targetActivity.type !== "post" ||
      !scopedRequest ||
      activeRequestRef.current !== scopedRequest
    ) {
      return;
    }
    const element = document.querySelector<HTMLElement>(
      `[data-post-content-id="${targetActivity.postContentId}"]`,
    );
    if (!element) return;

    const targetSelector = `[data-post-content-id="${targetActivity.postContentId}"]`;
    const scrollToTarget = () => {
      if (scopeKeyRef.current !== scopedRequest.scopeKey) return;
      document
        .querySelector<HTMLElement>(targetSelector)
        ?.scrollIntoView({ behavior: "auto", block: "center" });
    };

    scrollToTarget();
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        if (activeRequestRef.current !== scopedRequest) return;
        scrollToTarget();
        finish(targetActivity);

        // Resolving the request clears its parent state. Stream restoration and
        // pinned-post ordering can consequently run after the first scroll, so
        // anchor once more after those updates have committed.
        window.setTimeout(() => {
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(scrollToTarget);
          });
        }, 0);
      });
    });
  }, [finish, scopedRequest, targetActivity]);

  const notifyCommentLocated = useCallback(
    (commentId: number) => {
      if (
        !targetActivity ||
        targetActivity.type === "post" ||
        !scopedRequest ||
        activeRequestRef.current !== scopedRequest
      ) {
        return;
      }
      const locatedIndex =
        request?.activities.findIndex(
          (activity) => activity.type !== "post" && activity.id === commentId,
        ) ?? -1;
      if (locatedIndex < 0 || !request) return;
      resolutionKeysRef.current = request.activities
        .slice(locatedIndex)
        .map(activityKey);
      finish(request.activities[locatedIndex]);
    },
    [finish, request, scopedRequest, targetActivity],
  );

  const notifyCommentExhausted = useCallback(() => {
    if (
      !request ||
      !targetActivity ||
      targetActivity.type === "post" ||
      !scopedRequest ||
      activeRequestRef.current !== scopedRequest
    ) {
      return;
    }
    const currentIndex = request.activities.findIndex(
      (activity) => activityKey(activity) === activityKey(targetActivity),
    );
    if (currentIndex < 0) return;
    setTargetActivity(null);
    setTargetedPost(null);
    void locateFrom(currentIndex + 1);
  }, [locateFrom, request, scopedRequest, targetActivity]);

  return {
    targetedPost: isCurrentScope ? targetedPost : null,
    targetActivity: isCurrentScope ? targetActivity : null,
    highlightActivity: isCurrentScope ? highlightActivity : null,
    notifyCommentLocated,
    notifyCommentExhausted,
  };
};
